// Shared primitives for the Orders module (Dashboard + Parked Orders +
// Detail modal + AI Timeline modal).  Extracted so we don't duplicate 300
// lines of column-shape / status-tone / money-fmt logic across four files.
import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const token = () => localStorage.getItem("talmia_token") || "";

export function money(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "number") return `£${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const s = String(v);
  return s.startsWith("£") ? s : `£${s}`;
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return iso; }
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export const STATUS_TONE = {
  Completed:              "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  Shipped:                "text-teal-300 border-teal-500/40 bg-teal-500/10",
  "Awaiting Fulfillment": "text-amber-300 border-amber-500/40 bg-amber-500/10",
  "Awaiting Payment":     "text-orange-300 border-orange-500/40 bg-orange-500/10",
  "Awaiting Shipment":    "text-sky-300 border-sky-500/40 bg-sky-500/10",
  "Awaiting Pickup":      "text-sky-300 border-sky-500/40 bg-sky-500/10",
  "On Hold":              "text-purple-300 border-purple-500/40 bg-purple-500/10",
  Pending:                "text-purple-300 border-purple-500/40 bg-purple-500/10",
  Cancelled:              "text-slate-400 border-slate-500/40 bg-slate-500/10",
  Refunded:               "text-rose-300 border-rose-500/40 bg-rose-500/10",
  "Partially Refunded":   "text-rose-300 border-rose-500/40 bg-rose-500/10",
};

export const TIER_TONE = {
  BLACK:    "text-slate-200 bg-slate-800",
  PLATINUM: "text-cyan-100 bg-cyan-700/60",
  GOLD:     "text-amber-100 bg-amber-700/60",
  SILVER:   "text-slate-100 bg-slate-500/60",
  BRONZE:   "text-orange-100 bg-orange-700/60",
};

// Simple hover-tooltip that reveals a full-length note.  Used for the
// Staff Notes / Order Notes columns on the dashboard where the row only
// has ~2 lines of space.
export function HoverNote({ text, icon: Icon, maxRowChars = 40 }) {
  const [open, setOpen] = useState(false);
  if (!text) return <span className="text-slate-600 text-[10.5px] italic">—</span>;
  const truncated = text.length > maxRowChars ? `${text.slice(0, maxRowChars)}…` : text;
  return (
    <span className="relative inline-flex items-center gap-1 cursor-help min-w-0 max-w-full overflow-hidden"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {Icon && <Icon className="w-3 h-3 text-slate-500 shrink-0" />}
      <span className="text-slate-400 text-[10.5px] truncate min-w-0">{truncated}</span>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-40 w-80 max-h-72 overflow-y-auto rounded-lg border border-white/10 bg-[#0F1420]/98 backdrop-blur-md p-3 text-[11.5px] text-slate-200 shadow-2xl whitespace-pre-wrap">
          {text}
        </div>
      )}
    </span>
  );
}

// Wraps a REST GET with loading + error handling in one hook so the pages
// stay lean.  Not a general SWR replacement — just enough for our four
// orders screens.
export function useOrdersData(pathAndQuery, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}${pathAndQuery}`, { headers: { Authorization: `Bearer ${token()}` } });
      setData(r.data); setErr(null);
    } catch (e) {
      setErr(e); toast.error("Fetch failed", { description: e?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, deps);
  return { data, loading, err, reload: load };
}

// Post helper — POST /orders/{id}/ai-timeline etc.
export function useOrdersPost() {
  const busy = useRef(false);
  return async (path, body = {}) => {
    if (busy.current) return null;
    busy.current = true;
    try {
      const r = await axios.post(`${API}${path}`, body, {
        headers: { Authorization: `Bearer ${token()}` },
        timeout: 90000,
      });
      return r.data;
    } finally { busy.current = false; }
  };
}
